import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  extractDisplayRow,
  getIdempotencySummary,
  getImportPreviewColumns,
  getImportRowLineNumber,
  getImportRowStatus,
} from "@/lib/importFieldLabels";
import { trpc } from "@/lib/trpc";

interface ImportHistoryDetailsProps {
  importId: number;
  onClose?: () => void;
  onReenviar?: (importId: number) => void;
}

type ImportError = {
  rowNumber?: number;
  columnName?: string;
  message: string;
  value?: any;
  receivedValue?: string;
  rule?: string;
};

function getRowStatusBadgeClass(statusLabel: string): string {
  const normalized = statusLabel
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (
    normalized.includes("novo") ||
    normalized.includes("valida") ||
    normalized.includes("processada") ||
    normalized.includes("confirmada") ||
    normalized.includes("aprovada") ||
    normalized.includes("importada")
  ) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (
    normalized.includes("duplicada") ||
    normalized.includes("ignorada") ||
    normalized.includes("revisao") ||
    normalized.includes("processamento") ||
    normalized.includes("rascunho")
  ) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (
    normalized.includes("conflito") ||
    normalized.includes("erro") ||
    normalized.includes("rejeitada") ||
    normalized.includes("bloqueada")
  ) {
    return "border-red-200 bg-red-50 text-red-700";
  }

  return "border-slate-200 bg-slate-100 text-slate-700";
}

export const ImportHistoryDetails: React.FC<ImportHistoryDetailsProps> = ({
  importId,
  onClose,
  onReenviar,
}) => {
  const [currentErrorPage, setCurrentErrorPage] = useState(1);
  const [currentImportedPage, setCurrentImportedPage] = useState(1);
  const errorsPerPage = 20;
  const importedPerPage = 50;

  const { data: detailsData, isLoading, error } = trpc.imports.getHistoryDetails.useQuery({
    importHistoryId: importId,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 animate-pulse rounded bg-gray-200"></div>
        <div className="h-64 animate-pulse rounded bg-gray-200"></div>
      </div>
    );
  }

  if (error) {
    const isForbidden = (error as any)?.data?.code === "FORBIDDEN";
    const isNotFound = (error as any)?.data?.code === "NOT_FOUND";
    console.error("[ImportHistoryDetails] Erro ao carregar detalhes:", error);

    return (
      <Card className="border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <div className="text-xl text-red-500">⚠️</div>
          <div className="flex-1">
            {isForbidden ? (
              <>
                <p className="font-semibold text-red-700">Acesso negado</p>
                <p className="mt-1 text-sm text-red-600">Você não tem permissão para visualizar esta importação.</p>
              </>
            ) : isNotFound ? (
              <>
                <p className="font-semibold text-red-700">Importação não encontrada</p>
                <p className="mt-1 text-sm text-red-600">Este registro pode ter sido removido ou o ID é inválido.</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-red-700">Erro ao carregar detalhes</p>
                <p className="mt-1 text-sm text-red-600">{error.message}</p>
                <p className="mt-1 text-xs text-red-500">Tente novamente. Se o problema persistir, contate o suporte.</p>
              </>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          {!isForbidden && !isNotFound && (
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          )}
        </div>
      </Card>
    );
  }

  if (!detailsData?.data) {
    return (
      <Card className="p-6">
        <p className="text-gray-500">Importação não encontrada</p>
        <Button variant="outline" size="sm" onClick={onClose} className="mt-4">
          Voltar
        </Button>
      </Card>
    );
  }

  const importRecord = detailsData.data;
  const errors: ImportError[] = Array.isArray(importRecord.errorsSummary)
    ? importRecord.errorsSummary
    : [];
  const importedSample = Array.isArray(importRecord.importedSample)
    ? importRecord.importedSample
    : [];

  const totalErrorPages = Math.ceil(errors.length / errorsPerPage);
  const paginatedErrors = errors.slice(
    (currentErrorPage - 1) * errorsPerPage,
    currentErrorPage * errorsPerPage,
  );

  const totalImportedPages = Math.ceil(importedSample.length / importedPerPage);
  const paginatedImported = importedSample.slice(
    (currentImportedPage - 1) * importedPerPage,
    currentImportedPage * importedPerPage,
  );

  const suggestedColumns = getImportPreviewColumns(importedSample, 6);
  const importedColumns = suggestedColumns.length > 0
    ? suggestedColumns
    : ["Setor", "Cargo", "Quantidade de Pessoas", "Descrição das Atividades", "Jornada de Trabalho"];

  const importedRowsForDisplay = paginatedImported.map((row: Record<string, any>, idx: number) => {
    const absoluteRowNumber = (currentImportedPage - 1) * importedPerPage + idx + 2;
    const summary = getIdempotencySummary(row);
    const displayRow = extractDisplayRow(row, importedColumns);

    const hydratedDisplayRow = importedColumns.reduce<Record<string, string>>((acc, column) => {
      const currentValue = displayRow[column];

      if (currentValue && currentValue !== "—") {
        acc[column] = currentValue;
        return acc;
      }

      if (column === "Setor") {
        acc[column] = summary.setor;
        return acc;
      }

      if (column === "Cargo") {
        acc[column] = summary.cargo;
        return acc;
      }

      if (column === "Quantidade" || column === "Quantidade de Pessoas") {
        acc[column] = summary.quantidade;
        return acc;
      }

      acc[column] = currentValue || "—";
      return acc;
    }, {});

    return {
      key: String(row.rowHash || row.id || row._rowNumber || row.rowNumber || `${importId}-${absoluteRowNumber}-${idx}`),
      lineNumber: getImportRowLineNumber(row, absoluteRowNumber),
      status: getImportRowStatus(row) || "Válida no lote",
      displayRow: hydratedDisplayRow,
    };
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold">{importRecord.fileName}</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <p className="text-gray-600">Data</p>
            <p className="font-semibold">
              {format(new Date(importRecord.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Total de Linhas</p>
            <p className="font-semibold text-blue-700">{importRecord.totalRows}</p>
          </div>
          <div>
            <p className="text-gray-600">Linhas Válidas</p>
            <p className="font-semibold text-green-700">{importRecord.validRows}</p>
          </div>
          <div>
            <p className="text-gray-600">Linhas Inválidas</p>
            <p className="font-semibold text-red-700">{importRecord.invalidRows}</p>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="importados" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="importados">
            Linhas inspecionadas ({importedSample.length})
          </TabsTrigger>
          <TabsTrigger value="erros">
            Erros ({errors.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="importados" className="space-y-4">
          {importedSample.length === 0 ? (
            <Card className="p-6 text-center text-gray-500">
              Nenhuma linha inspecionada disponível neste lote.
            </Card>
          ) : (
            <>
              <Card className="border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                Use esta amostra para conferir rapidamente o que entrou no lote. A grade abaixo prioriza os campos mais úteis para dupla checagem, como linha, situação e dados principais da planilha.
              </Card>

              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700">
                      <th className="border-b p-3 text-left font-medium">Linha</th>
                      <th className="border-b p-3 text-left font-medium">Status</th>
                      {importedColumns.map((column) => (
                        <th key={column} className="border-b p-3 text-left font-medium">
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importedRowsForDisplay.map((row) => (
                      <tr key={row.key} className="align-top hover:bg-slate-50">
                        <td className="border-b p-3 font-medium text-slate-900">#{row.lineNumber}</td>
                        <td className="border-b p-3">
                          <Badge variant="outline" className={getRowStatusBadgeClass(row.status)}>
                            {row.status}
                          </Badge>
                        </td>
                        {importedColumns.map((column) => (
                          <td key={`${row.key}-${column}`} className="border-b p-3 text-slate-700">
                            <div className="max-w-xs whitespace-pre-wrap break-words text-sm leading-5">
                              {row.displayRow[column] || "—"}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalImportedPages > 1 && (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentImportedPage === 1}
                    onClick={() => setCurrentImportedPage(currentImportedPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center px-3 text-sm text-gray-600">
                    Página {currentImportedPage} de {totalImportedPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentImportedPage >= totalImportedPages}
                    onClick={() => setCurrentImportedPage(currentImportedPage + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}

              {importedSample.length > importedPerPage && (
                <p className="text-center text-xs text-gray-500">
                  Mostrando {paginatedImported.length} de {importedSample.length} linhas inspecionadas.
                </p>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="erros" className="space-y-4">
          {errors.length === 0 ? (
            <Card className="border-green-200 bg-green-50 p-6 text-center">
              <p className="font-semibold text-green-700">✓ Nenhum erro encontrado</p>
              <p className="text-sm text-green-600">Todas as linhas foram validadas com sucesso.</p>
            </Card>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedErrors.map((errorItem: ImportError, idx: number) => (
                  <Card key={idx} className="border-l-4 border-l-red-500 p-4">
                    <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                      <div>
                        <p className="text-gray-600">Linha</p>
                        <p className="font-semibold text-red-700">#{errorItem.rowNumber}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Coluna</p>
                        <p className="font-semibold">{errorItem.columnName || "—"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-gray-600">Valor Informado</p>
                        <p className="break-words rounded bg-gray-100 p-2 font-mono text-xs">
                          {errorItem.receivedValue || errorItem.value || "(vazio)"}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <p className="text-gray-600">Regra</p>
                        <p className="text-sm">{errorItem.rule || "—"}</p>
                      </div>
                      {errorItem.message && (
                        <div className="md:col-span-2">
                          <p className="text-gray-600">Mensagem</p>
                          <p className="text-sm text-red-700">{errorItem.message}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              {totalErrorPages > 1 && (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentErrorPage === 1}
                    onClick={() => setCurrentErrorPage(currentErrorPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center px-3 text-sm text-gray-600">
                    Página {currentErrorPage} de {totalErrorPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentErrorPage >= totalErrorPages}
                    onClick={() => setCurrentErrorPage(currentErrorPage + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button onClick={() => onReenviar?.(importId)}>Reenviar Importação</Button>
      </div>
    </div>
  );
};
